import { SerializedData, Store } from "bernie-core";
import { Logger } from "bernie-logger";
import { PropertyFiltersQuery } from "__generated__/api";
import _SortAndFilter = PropertyFiltersQuery._SortAndFilter;
import { Filter } from "components/flexComponents/PropertyFilters/typings";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { PROPERTY_FILTERS_COMPONENT_MAP } from "components/shared/PropertyFIlterComponents/map/PropertyFiltersComponentMap";
import { Radio } from "components/shared/PropertyFIlterComponents/components/Radio";
import { action, observable } from "mobx";
import { PropertyFilterPublisher } from "./PropertyFilterPublisher";
import { HotelsStore } from "../HotelsStore";
import { Options } from "src/components/flexComponents/PropertyFilters/typings";

const validFilterNames = Object.keys(PROPERTY_FILTERS_COMPONENT_MAP);

export type SelectedFilters = {
  name: string;
  options: Options[];
};

function getFilterName(name: string) {
  switch (name) {
    case "star":
      return "starList";
    case "guestRating":
      return "reviewScore";
    case "lodging":
      return "structureTypes";
    case "regionId":
      return "neighborhood";
    default:
      return name;
  }
}

/**
 * Class that describes the state of the filters. This will be used as the source of truth.
 *
 * filterName refers to name of the "category" (e.g. Property Class)
 * optionName refers to the individual options for a filter (e.g. 5 stars)
 */
export class PropertyFiltersStore extends Store {
  // e.g. ordered map of key = "propertyClass", value = map of 1 star, 2 star, etc
  private publisher = new PropertyFilterPublisher();

  @observable
  private filtersMap = new Map<string, Filter>();

  @observable
  private originalRegionId: string;

  @observable
  private isFrozen = false;

  @observable
  private frozenFilter: string[];

  public constructor(state: SerializedData, logger: Logger) {
    super(state, logger);
  }

  public hydrate(data: SerializedData): void {
    const filtersMap = new Map(
      (data.filtersMap as any[]).map(([k, f]) => {
        return [k, { ...f, options: new Map(f.options) }];
      })
    );

    Object.assign(this, { ...data, filtersMap });
    this.syncFilters();
  }

  public toJSON() {
    /**
     * We are parsing the Map as a Tuple to prevent order changes that happend if we send it as a JSON
     */
    const filters = Array.from(this.filtersMap.keys()).map((filtersMapKey) => {
      const options = Array.from(this.filtersMap.get(filtersMapKey)!.options!).map(([optionKey, option]) => {
        return [optionKey, option];
      });

      return [filtersMapKey, { ...this.filtersMap.get(filtersMapKey), options }];
    });

    return {
      filtersMap: filters,
      originalRegionId: String(this.originalRegionId),
    };
  }

  @action
  public frezze() {
    this.isFrozen = true;
    this.frozenFilter = this.getListOfFilters().reduce<string[]>((selectedFilters, filter) => {
      const options = Array.from(filter.options.values())
        .filter(this.isOptionSelected.bind(this))
        .map((option) => option.selectedLabel!);

      return [...selectedFilters, ...options];
    }, []);
  }

  @action
  public defrost() {
    this.isFrozen = false;
    this.frozenFilter = [];
  }

  @action
  public resetFilters() {
    this.resetAllFilters();
    this.frozenFilter.forEach((filter) => this.publisher.next(filter, true));
    this.defrost();
  }

  @action
  public setFilters(filters: _SortAndFilter[]) {
    this.filtersMap = new Map<string, Filter>(
      filters
        .filter((filter) => filter?.options && filter?.label && validFilterNames.includes(filter?.name))
        .map((filter) => {
          const { name, label, options } = filter;

          const optionsMap = new Map<string, Options>(
            options.map((option) => {
              const isDefault = option.optionValue === this.originalRegionId || option.optionValue === "";

              return [option.optionValue!, { ...option, isDefault }];
            })
          );

          return [name, { name, label: label!, options: optionsMap }];
        })
    );
    this.syncFilters();
  }

  @action
  public syncFilters() {
    this.filtersMap.forEach((filter) => {
      filter.options.forEach((option) => {
        this.publisher.subscribe(option.selectedLabel!, (value: boolean) => {
          option.isSelected = value;
        });
      });
    });
  }

  @action
  public setRegion(region: string) {
    this.originalRegionId = String(region);
  }

  public getListOfFilters() {
    return Array.from(this.filtersMap.values());
  }

  public getListOfPopularFilters() {
    const popularFilters = this.filtersMap.get("popularFilter");

    return Array.from(popularFilters?.options.values() || []);
  }

  public getFreeCancellationFilter() {
    const paymentType = this.filtersMap.get("paymentType");
    const freeCancellation = paymentType?.options.get("FREE_CANCELLATION");

    return freeCancellation;
  }

  public isEmpty() {
    return this.filtersMap.size === 0;
  }

  public getNumberOfSelectedFilters() {
    if (this.isFrozen) {
      return this.frozenFilter.length;
    }

    return this.getListOfFilters().reduce((count, filter) => {
      return count + this.getNumberOfSelectedFiltersPerCategory(filter.name);
    }, 0);
  }

  public getNumberOfSelectedFiltersPerCategory(filterName: string) {
    const filters = this.filtersMap.get(filterName);
    const options = Array.from(filters?.options.values() || []);

    return options.filter(this.isOptionSelected.bind(this)).length;
  }

  private isOptionSelected(option: Options) {
    return (
      !option.filterCategory &&
      option.isSelected &&
      option.optionValue !== this.originalRegionId &&
      option.optionValue !== ""
    );
  }

  public isSelected(filterName: string, optionName: string) {
    return this.filtersMap.get(filterName)?.options.get(optionName)?.isSelected;
  }

  @action
  public toggleSelection = (filterName: string, optionName: string) => {
    if (!this.filtersMap.get(filterName)?.options.get(optionName)) return;

    const option = this.filtersMap.get(filterName)!.options.get(optionName)!;
    const value = !option.isSelected;

    this.setFilterStatus(filterName, optionName, value);

    const isRegionId = option.filterCategory === "regionId" || filterName === "regionId";

    if (isRegionId && this.filtersMap.get(filterName)!.options.get(optionName)!.isSelected) {
      return this.filtersMap.get(filterName)!.options.get(optionName)!.optionValue;
    } else {
      return this.originalRegionId;
    }
  };

  @action
  public setFilterStatus = (filterName: string, optionName: string, value: boolean) => {
    const option = this.filtersMap.get(filterName)!.options.get(optionName)!;
    const groupName = option.filterCategory || filterName;

    if (PROPERTY_FILTERS_COMPONENT_MAP[groupName] === Radio) {
      if (value) {
        this.clearFilters(groupName);
      } else {
        this.resetPerCategory(groupName);
      }
    }

    this.publisher.next(option.selectedLabel!, value);
  };

  @action
  public resetPerCategory(groupName: string) {
    this.filtersMap.get(groupName)!.options.forEach((filterOption) => {
      const value = filterOption.isDefault; // set true or false according to default config

      this.publisher.next(filterOption.selectedLabel!, value);
    });
  }

  @action
  public resetAllFilters() {
    this.getListOfFilters().forEach((filter) =>
      filter.options.forEach((option) => {
        this.publisher.next(option.selectedLabel!, false);

        if (option.isDefault) {
          this.publisher.next(option.selectedLabel!, true);
        }
      })
    );
  }

  @action
  public clearFilters(filterName: string) {
    if (!this.filtersMap.get(filterName)?.options) {
      return;
    }

    this.filtersMap.get(filterName)!.options.forEach((option) => {
      this.publisher.next(option.selectedLabel!, false);
    });
  }

  public getListOfSelectedFilters() {
    const selectedFilters: SelectedFilters[] = [];

    this.getListOfFilters().map((filter) => {
      const options = Array.from(filter.options.values());
      const selectedOptions = options.filter((option) => this.isOptionSelected(option));

      if (selectedOptions.length > 0) {
        selectedFilters.push({
          name: filter.name,
          options: selectedOptions,
        });
      }
    });

    return selectedFilters;
  }

  public buildLinkToSRP(context?: ExtendedContextStore) {
    const link = new HotelSearchLinkBuilder()
      .withStayLength()
      .withDaysInFuture()
      .withRegionId(this.originalRegionId)
      .withExpandForm();

    // Add filters
    this.getListOfFilters().forEach((filter) => {
      Array.from(filter.options.values())
        .filter((option) => option.isSelected)
        .forEach((option) => {
          // We dont want to get popular filters since they are propagated
          if (option.filterCategory) return;

          link.withFilter(filter.name, option.optionValue || "");
        });
    });

    return link.build(context);
  }

  public getSelectedFilters() {
    const getValue = (name: string, value?: string | null) => {
      if (!value) return null;
      if (name === "starList") return Number(value);

      return value;
    };

    const filters = this.getListOfFilters().reduce<{ [key: string]: any | any[] }>((selectedFilters, filter) => {
      filter.options.forEach((option) => {
        /* Exclude not selected and popular filters options*/
        if (!option.isSelected || option.filterCategory) return;

        const bexApiName = getFilterName(filter.name);
        const isRadio = PROPERTY_FILTERS_COMPONENT_MAP[filter.name] === Radio;

        if (option.range) {
          if (!selectedFilters[bexApiName]) {
            selectedFilters[bexApiName] = { min: option.range.min, max: option.range.max };

            return;
          }

          const prev = selectedFilters[bexApiName] as Pick<PropertyFiltersQuery.Range, "max" | "min">;

          selectedFilters[bexApiName] = {
            min: option.range.min! < prev.min! ? option.range.min : prev.min,
            max: option.range.max! > prev.max! ? option.range.max : prev.max,
          };
        } else if (isRadio) {
          selectedFilters[bexApiName] = getValue(bexApiName, option.optionValue);
        } else {
          const prev = selectedFilters[bexApiName] || [];
          selectedFilters[bexApiName] = [getValue(bexApiName, option.optionValue), ...prev];
        }
      });

      return selectedFilters;
    }, {});

    return filters;
  }

  @action
  public applyFilters(hotels: HotelsStore, context: ExtendedContextStore, isImmediateFilteringEnabled: boolean = true) {
    if (typeof window === "undefined") return;

    if (isImmediateFilteringEnabled) {
      hotels.fetchFilteredProperties(context, this.getSelectedFilters());
    } else {
      window.location.assign(this.buildLinkToSRP());
    }
  }
}
